import { useState, useEffect } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { Plus, X, Save, Trash2, Tag as TagIcon, Star, GripVertical } from 'lucide-react';

const CATEGORIES = [
  { key: 'occasion', label: 'Besonderer Anlass' },
  { key: 'family', label: 'Familie' },
  { key: 'seating', label: 'Platzwünsche' },
  { key: 'service', label: 'Service' },
  { key: 'vip', label: 'VIP' },
  { key: 'other', label: 'Sonstiges' },
];

const PRESETS = [
  { name: 'Geburtstag', category: 'occasion', icon: '🎂', color: '#EC4899', is_vip: true },
  { name: 'Hochzeitstag', category: 'occasion', icon: '💍', color: '#EC4899', is_vip: true },
  { name: 'Jubiläum', category: 'occasion', icon: '🎉', color: '#A855F7', is_vip: true },
  { name: 'Geschäftsessen', category: 'occasion', icon: '💼', color: '#3B82F6' },
  { name: 'Erstbesuch', category: 'occasion', icon: '✨', color: '#EAB308' },
  { name: 'Kinderstuhl', category: 'family', icon: '🪑', color: '#22C55E' },
  { name: 'Kinderwagen', category: 'family', icon: '🍼', color: '#22C55E' },
  { name: 'Baby dabei', category: 'family', icon: '👶', color: '#22C55E' },
  { name: 'Ruhiger Tisch', category: 'seating', icon: '🤫', color: '#3B82F6' },
  { name: 'Fensterplatz', category: 'seating', icon: '🪟', color: '#3B82F6' },
  { name: 'Terrasse', category: 'seating', icon: '☀️', color: '#F97316' },
  { name: 'Innenbereich', category: 'seating', icon: '🏠', color: '#3B82F6' },
  { name: 'Schattenplatz', category: 'seating', icon: '🌳', color: '#22C55E' },
  { name: 'Rollstuhl', category: 'service', icon: '♿', color: '#3B82F6' },
  { name: 'Hund dabei', category: 'service', icon: '🐶', color: '#F97316' },
  { name: 'Allergie-Hinweis', category: 'service', icon: '⚠️', color: '#EF4444' },
  { name: 'Vegane Optionen', category: 'service', icon: '🌱', color: '#22C55E' },
  { name: 'VIP-Gast', category: 'vip', icon: '⭐', color: '#A855F7', is_vip: true },
];

const COLORS = ['#5C5C59', '#EC4899', '#A855F7', '#3B82F6', '#22C55E', '#EAB308', '#F97316', '#EF4444', '#06B6D4'];

export default function AdminTags() {
  const [tags, setTags] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editTag, setEditTag] = useState(null);
  const [filter, setFilter] = useState('');

  const load = async () => {
    try {
      const r = await api.get('/tags');
      setTags(r.data);
    } catch { toast.error('Fehler beim Laden'); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (t) => {
    if (!window.confirm(`Tag "${t.name}" löschen? Wird aus allen Reservierungen entfernt.`)) return;
    try {
      await api.delete(`/tags/${t.id}`);
      toast.success('Gelöscht');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
  };

  const toggleActive = async (t) => {
    try {
      await api.put(`/tags/${t.id}`, { active: !t.active });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
  };

  const seedPresets = async () => {
    if (!window.confirm(`${PRESETS.length} vordefinierte Tags anlegen?`)) return;
    let created = 0;
    for (let i = 0; i < PRESETS.length; i++) {
      try {
        await api.post('/tags', { ...PRESETS[i], sort_order: i * 10, active: true });
        created++;
      } catch {}
    }
    toast.success(`${created} Tags angelegt`);
    load();
  };

  const filtered = filter ? tags.filter(t => t.category === filter) : tags;
  const grouped = CATEGORIES.map(c => ({
    ...c,
    tags: filtered.filter(t => t.category === c.key),
  })).filter(c => c.tags.length > 0);

  return (
    <div className="p-4 md:p-8 max-w-5xl" data-testid="admin-tags-page">
      <header className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#5C5C59]">Gästewünsche</p>
          <h1 className="font-display text-3xl text-[#1C1C19]">Tag-System</h1>
          <p className="text-sm text-[#5C5C59] mt-2 max-w-2xl">
            Strukturierte Wünsche, die der Gast bei der Reservierung auswählen kann (Geburtstag, Kinderstuhl, Hund dabei usw.).
            VIP-Tags werden in Tagesübersicht und Tischplan hervorgehoben.
          </p>
        </div>
        <div className="flex gap-2">
          {tags.length === 0 && (
            <button onClick={seedPresets} data-testid="tags-seed-btn" className="border border-[#1C1C19] text-[#1C1C19] hover:bg-[#1C1C19] hover:text-white px-4 py-2.5 rounded text-sm">
              Vorlagen importieren
            </button>
          )}
          <button onClick={() => { setEditTag(null); setShowForm(true); }} data-testid="tags-add-btn" className="bg-[#1C1C19] hover:bg-[#3A3A35] text-white px-4 py-2.5 rounded text-sm flex items-center gap-2">
            <Plus className="w-4 h-4" /> Neuer Tag
          </button>
        </div>
      </header>

      <div className="mb-4 flex flex-wrap gap-1">
        <button onClick={() => setFilter('')} data-testid="tags-cat-all" className={`px-3 py-1.5 rounded-full text-xs border ${!filter ? 'bg-[#1C1C19] text-white border-[#1C1C19]' : 'border-[#E5E5E0] text-[#5C5C59] hover:border-[#1C1C19]'}`}>
          Alle ({tags.length})
        </button>
        {CATEGORIES.map(c => {
          const n = tags.filter(t => t.category === c.key).length;
          if (n === 0) return null;
          return (
            <button key={c.key} onClick={() => setFilter(c.key)} data-testid={`tags-cat-${c.key}`} className={`px-3 py-1.5 rounded-full text-xs border ${filter === c.key ? 'bg-[#1C1C19] text-white border-[#1C1C19]' : 'border-[#E5E5E0] text-[#5C5C59] hover:border-[#1C1C19]'}`}>
              {c.label} ({n})
            </button>
          );
        })}
      </div>

      {tags.length === 0 && (
        <div className="bg-white border border-dashed border-[#E5E5E0] rounded-lg p-12 text-center" data-testid="tags-empty">
          <TagIcon className="w-10 h-10 mx-auto text-[#9CA3AF] mb-3" />
          <p className="text-[#1C1C19] font-medium mb-1">Noch keine Tags definiert</p>
          <p className="text-sm text-[#5C5C59] mb-4">Importieren Sie unsere Vorlage oder legen Sie eigene an.</p>
          <button onClick={seedPresets} className="bg-[#1C1C19] text-white px-4 py-2 rounded text-sm">Vorlagen importieren</button>
        </div>
      )}

      <div className="space-y-6">
        {grouped.map(cat => (
          <section key={cat.key} data-testid={`tags-group-${cat.key}`}>
            <h2 className="font-display text-lg mb-2 flex items-center gap-2">
              {cat.label}
              <span className="text-xs text-[#5C5C59] font-mono">({cat.tags.length})</span>
            </h2>
            <div className="bg-white border border-[#E5E5E0] rounded-lg divide-y divide-[#E5E5E0]">
              {cat.tags.map(t => (
                <div key={t.id} className={`p-4 flex items-center gap-3 ${!t.active ? 'opacity-50' : ''}`} data-testid={`tag-row-${t.id}`}>
                  <GripVertical className="w-4 h-4 text-[#9CA3AF]" />
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center text-xl flex-shrink-0"
                    style={{ background: (t.color || '#5C5C59') + '20' }}
                  >
                    {t.icon || '🏷️'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-medium text-[#1C1C19]">{t.name}</h3>
                      {t.is_vip && (
                        <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wider" style={{ background: '#A855F720', color: '#A855F7' }}>
                          <Star className="w-2.5 h-2.5" /> VIP
                        </span>
                      )}
                      {!t.active && <span className="text-[10px] px-1.5 py-0.5 bg-[#9CA3AF]/20 text-[#5C5C59] rounded uppercase tracking-wider">Inaktiv</span>}
                    </div>
                    <p className="text-xs text-[#5C5C59] mt-0.5 font-mono">Sortierung: {t.sort_order}</p>
                  </div>
                  <button onClick={() => toggleActive(t)} data-testid={`tag-toggle-${t.id}`} className="px-3 py-1.5 border border-[#E5E5E0] rounded text-xs hover:border-[#1C1C19]">
                    {t.active ? 'Deaktivieren' : 'Aktivieren'}
                  </button>
                  <button onClick={() => { setEditTag(t); setShowForm(true); }} data-testid={`tag-edit-${t.id}`} className="px-3 py-1.5 border border-[#E5E5E0] rounded text-xs hover:border-[#1C1C19]">
                    Bearbeiten
                  </button>
                  <button onClick={() => remove(t)} data-testid={`tag-delete-${t.id}`} className="p-2 hover:bg-[#FEE2E2] rounded">
                    <Trash2 className="w-4 h-4 text-[#EF4444]" />
                  </button>
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>

      {showForm && (
        <TagForm
          tag={editTag}
          onClose={() => { setShowForm(false); setEditTag(null); }}
          onSaved={() => { setShowForm(false); setEditTag(null); load(); }}
        />
      )}
    </div>
  );
}

function TagForm({ tag, onClose, onSaved }) {
  const [form, setForm] = useState(tag || {
    name: '', category: 'occasion', icon: '', color: '#5C5C59',
    active: true, is_vip: false, sort_order: 0,
  });
  const [submitting, setSubmitting] = useState(false);

  const save = async () => {
    if (!form.name.trim()) { toast.error('Name erforderlich'); return; }
    setSubmitting(true);
    try {
      if (tag) await api.put(`/tags/${tag.id}`, form);
      else await api.post('/tags', form);
      toast.success(tag ? 'Aktualisiert' : 'Erstellt');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 bg-[#1C1C19]/40 z-50 flex items-center justify-center p-4" onClick={() => !submitting && onClose()}>
      <div className="bg-white rounded-lg max-w-md w-full p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="tag-form">
        <div className="flex items-start justify-between mb-4">
          <h2 className="font-display text-xl">{tag ? 'Tag bearbeiten' : 'Neuer Tag'}</h2>
          <button onClick={onClose} className="p-1 text-[#5C5C59]"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-xs uppercase tracking-wider text-[#5C5C59] block mb-1">Name *</label>
            <input
              value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
              data-testid="tag-name-input"
              className="w-full px-3 py-2 border border-[#E5E5E0] rounded focus:outline-none focus:border-[#1C1C19]"
              placeholder="z.B. Geburtstag"
              autoFocus
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#5C5C59] block mb-1">Kategorie</label>
            <select
              value={form.category}
              onChange={e => setForm({ ...form, category: e.target.value })}
              data-testid="tag-category-select"
              className="w-full px-3 py-2 border border-[#E5E5E0] rounded bg-white"
            >
              {CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#5C5C59] block mb-1">Icon (Emoji)</label>
            <input
              value={form.icon}
              onChange={e => setForm({ ...form, icon: e.target.value })}
              data-testid="tag-icon-input"
              maxLength={4}
              className="w-20 px-3 py-2 border border-[#E5E5E0] rounded text-center text-xl"
              placeholder="🎂"
            />
            <p className="text-[10px] text-[#5C5C59] mt-1">Tipp: Win+. (Windows) oder Cmd+Ctrl+Space (Mac) für Emoji-Auswahl.</p>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#5C5C59] block mb-1">Farbe</label>
            <div className="flex flex-wrap gap-1">
              {COLORS.map(c => (
                <button
                  key={c}
                  onClick={() => setForm({ ...form, color: c })}
                  data-testid={`tag-color-${c}`}
                  className={`w-8 h-8 rounded-full border-2 ${form.color === c ? 'border-[#1C1C19]' : 'border-transparent'}`}
                  style={{ background: c }}
                />
              ))}
              <input type="color" value={form.color} onChange={e => setForm({ ...form, color: e.target.value })} className="w-8 h-8 rounded cursor-pointer" />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#5C5C59] block mb-1">Sortierung</label>
            <input
              type="number"
              value={form.sort_order}
              onChange={e => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })}
              data-testid="tag-sort-input"
              className="w-24 px-3 py-2 border border-[#E5E5E0] rounded"
            />
            <p className="text-[10px] text-[#5C5C59] mt-1">Kleinere Werte erscheinen zuerst.</p>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.is_vip ?? false} onChange={e => setForm({ ...form, is_vip: e.target.checked })} data-testid="tag-vip-checkbox" />
            <Star className="w-3.5 h-3.5 text-[#A855F7]" /> VIP-Tag (hervorgehoben in Floor-Plan + Tagesübersicht)
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.active ?? true} onChange={e => setForm({ ...form, active: e.target.checked })} data-testid="tag-active-checkbox" />
            Aktiv (für Gäste sichtbar)
          </label>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} disabled={submitting} data-testid="tag-cancel-btn" className="px-4 py-2 border border-[#E5E5E0] rounded text-sm">Abbrechen</button>
          <button onClick={save} disabled={submitting} data-testid="tag-save-btn" className="bg-[#1C1C19] hover:bg-[#3A3A35] text-white px-4 py-2 rounded text-sm flex items-center gap-2">
            <Save className="w-4 h-4" /> {submitting ? 'Speichere...' : 'Speichern'}
          </button>
        </div>
      </div>
    </div>
  );
}
